import styled, { css, keyframes } from 'styled-components';
import { createStatic } from 'styled-components-breakpoint';
import { BORDER_RADIUS, isFocused, media, mediaMap, OVERLAYS } from '@belong/themes';
import { ButtonWithRef } from '../../actions/Button/ButtonBase';
import { PictureContainer } from '../../media/PictureContainer';
import ButtonGroup from '../../styles/ButtonGroup';
import { UICard } from '../../styles/UICard';
import { Column } from '../../ui';

const customBreakpoints = createStatic({ xs: 320 });

const SVG_SIZE = {
  xs: '4.8rem',
  md: '7.2rem'
};

// Used for adding some padding to the close button
// It's too small
const CLOSE_BUTTON_PADDING = '0.8rem';

// Position the close button in the top right corner of the dialog box.
const CLOSE_BUTTON_POSITION = css`
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  z-index: 1;

  ${media('md')`
    top: 1.6rem;
    right: 1.6rem;
  `}
`;

const FadeIn = keyframes`
  from {
    transform: scale(0.25);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const FadeOut = keyframes`
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0.25);
    opacity: 0;
  }
`;

export const Dialog = styled(Column).attrs({ as: 'dialog' })<{ isOpen: boolean }>`
  max-height: 80vh;
  max-width: calc(100% - 1.6rem);
  border: none;
  padding: 0;
  border-radius: ${BORDER_RADIUS.CARD};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  animation: ${({ isOpen }) => (isOpen ? FadeIn : FadeOut)} 0.2s linear;
  transition: visibility 0.2s linear;

  ${mediaMap(
    { md: '80.4rem' },
    (maxWidth: string) => css`
      max-width: calc(${maxWidth} - 6px);
    `
  )}

  &:before {
    display: none;
    content: '';
    width: 100%;
    height: 0.4rem;
    position: absolute;
    top: 0;
    z-index: 1;
  }

  &::backdrop {
    background: ${OVERLAYS.BLACK};
  }

  &:focus-visible {
    ${isFocused()}
  }
`;

export const DialogBox = styled(UICard)`
  position: relative;
  padding: 2rem 1rem;
  display: flex;
  flex-direction: column;

  ${customBreakpoints.xs`
    padding: 2.4rem 1.6rem;
  `}

  ${media('md')`
    padding: 2.4rem
  `}
`;

export const DialogHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  margin: 0 0 1.6rem;

  ${media('md')`
    margin: 0 0 2.4rem;
  `}
`;

export const CloseButton = styled(ButtonWithRef)`
  padding: ${CLOSE_BUTTON_PADDING};
  line-height: 0;
  background-color: transparent;
  border-color: transparent;
  ${CLOSE_BUTTON_POSITION}

  &:hover,
  &:active {
    background-color: transparent;
    border-color: transparent;
  }
`;

export const DialogBody = styled.div`
  > * + * {
    margin-top: 1.6rem;
  }
`;

export const Image = styled(PictureContainer)`
  margin-bottom: 1.2rem;
  object-fit: contain;
  ${mediaMap(
    SVG_SIZE,
    (size: string) => css`
      width: ${size};
      height: ${size};
    `
  )}
`;

export const DialogFooter = styled.div`
  margin-top: 2.4rem;

  && > ${ButtonGroup} {
    justify-content: 'flex-start';
  }

  ${media('md')`
    margin-top: 3.2rem
  `}
`;
